import { DateTime } from "luxon";
import { loanApi } from "@api/loans";

// Constants
export const FINE_RATE_PER_DAY = 10;
export const SUCCESS_MESSAGE_DURATION = 8000;
export const SEARCH_DEBOUNCE_DELAY = 300;
export const REFRESH_DELAY_AFTER_RETURN = 3000;
export const REFRESH_DELAY_AFTER_PAYMENT = 1500;
export const DEFAULT_MESSAGES = {
  SUCCESS_PAYMENT: "Payment submitted successfully",
  SUCCESS_WITH_UPDATE: "Payment submitted successfully and book status updated",
  ERROR_PAYMENT: "Error submitting payment. Please try again.",
  ERROR_NO_IMAGE: "Please upload a payment receipt image",
  ERROR_NO_BOOKS: "Please select at least one book to pay for",
  ERROR_FILE_READ: "Failed to read the selected file",
  ERROR_PATRON: "Failed to load patron data",
  LOAN_PROCESSING: "Book return is being processed.",
};

/**
 * Calculate business days between two dates (excluding weekends)
 * @param {Date|string} startDate - Start date (Date object or ISO string)
 * @param {Date|string} endDate - End date (Date object or ISO string)
 * @returns {number} Number of business days between dates
 */
export const calculateBusinessDays = (startDate, endDate) => {
  let start = startDate instanceof Date ? startDate : new Date(startDate);
  let end = endDate instanceof Date ? endDate : new Date(endDate);

  // Normalize to midnight for consistent calculations
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  if (end <= start) return 0;

  const timeDiff = end.getTime() - start.getTime();
  const totalDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

  let businessDays = 0;
  const currentDate = new Date(start);

  for (let i = 0; i < totalDays; i++) {
    currentDate.setDate(currentDate.getDate() + 1);
    const dayOfWeek = currentDate.getDay();
    // Exclude weekends (0 = Sunday, 6 = Saturday)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDays++;
    }
  }

  return businessDays;
};

/**
 * Calculate business days between two dates using Luxon
 * @param {string|DateTime} startDate - Start date (ISO string or Luxon DateTime)
 * @param {string|DateTime} endDate - End date (ISO string or Luxon DateTime)
 * @returns {number} Number of business days
 */
export const calculateBusinessDaysLuxon = (startDate, endDate) => {
  const start =
    typeof startDate === "string" ? DateTime.fromISO(startDate) : startDate;
  const end = typeof endDate === "string" ? DateTime.fromISO(endDate) : endDate;

  if (end <= start) return 0;

  let businessDays = 0;
  let current = start.plus({ days: 1 });

  while (current <= end) {
    // Luxon weekday: 1 = Monday, 7 = Sunday
    if (current.weekday >= 1 && current.weekday <= 5) {
      businessDays++;
    }
    current = current.plus({ days: 1 });
  }

  return businessDays;
};

/**
 * Calculate the number of overdue days for a loan
 * @param {string} endDate - ISO date string for loan end date
 * @returns {number} Number of overdue days
 */
export const calculateOverdueDays = (endDate) => {
  const loanEndDate = DateTime.fromISO(endDate);
  const today = DateTime.now();
  return Math.max(0, Math.floor(today.diff(loanEndDate, "days").days));
};
/**
 * Calculate overdue fine based on days overdue
 * @param {string} endDate - ISO date string for loan end date
 * @param {number} fineRate - Fine rate per day (default: FINE_RATE_PER_DAY)
 * @returns {number} Calculated fine amount
 */
export const calculateOverdueFine = (endDate, fineRate = FINE_RATE_PER_DAY) => {
  const overdueDays = calculateOverdueDays(endDate);
  return overdueDays * fineRate;
};

/**
 * Check if a loan is overdue
 * @param {string} endDate - ISO date string for loan end date
 * @returns {boolean} True if loan is overdue
 */
export const isLoanOverdue = (endDate) => {
  return calculateOverdueDays(endDate) > 0;
};

/**
 * Format date for display using Luxon
 * @param {string} isoDate - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDateForDisplay = (isoDate) => {
  return DateTime.fromISO(isoDate).toLocaleString();
};

/**
 * Create FormData for payment submission
 * @param {Object} params - Payment parameters
 * @param {string} params.patronId - Patron ID
 * @param {number} params.amount - Payment amount
 * @param {Array} params.selectedLoanIds - Array of selected loan IDs
 * @param {Array} params.loanAmountDetails - Detailed amount breakdown per loan
 * @param {File|string} params.paymentReceipt - Payment receipt file or reference
 * @returns {FormData} Formatted form data for API submission
 */
export const createPaymentFormData = ({
  patronId,
  amount,
  selectedLoanIds,
  loanAmountDetails,
  paymentReceipt,
}) => {
  const formData = new FormData();
  formData.append("profile_id", patronId);
  formData.append("amount", amount.toFixed(2));
  formData.append("loan_ids", JSON.stringify(selectedLoanIds));
  formData.append("loan_amount_details", JSON.stringify(loanAmountDetails));

  if (paymentReceipt) {
    if (typeof paymentReceipt === "string") {
      formData.append("payment_reference", paymentReceipt);
    } else {
      formData.append("payment_receipt", paymentReceipt);
    }
  }

  return formData;
};

/**
 * Process loan status update after return
 * @param {string} loanId - Loan ID to process
 * @returns {Promise<Object>} Result object with success status and updated loan data
 */
export const processLoanStatusUpdate = async (loanId) => {
  try {
    const loanResponse = await loanApi.get(loanId);
    const loan = loanResponse.data;
    const originalDueDate = loan.metadata.end_date;

    await loanApi.doAction(
      `/circulation/loans/${loanId}/checkin`,
      loan.document_pid,
      loan.metadata.patron_pid,
      { itemPid: loan.metadata.item_pid }
    );

    await loanApi.updateDates(loanId, { endDate: originalDueDate });
    const updatedLoanResponse = await loanApi.get(loanId);

    return {
      success: true,
      action: "checkin",
      updatedLoan: updatedLoanResponse.data,
    };
  } catch (error) {
    return { success: false, error };
  }
};

/**
 * Check loan status - determines if loan is returned or still active
 * @param {string} loanId - Loan ID to check
 * @returns {Promise<string>} Loan status
 */
export const checkLoanStatus = async (loanId) => {
  try {
    let loan = null;

    // Try direct API call first
    try {
      const response = await loanApi.get(loanId);
      loan = response.data;
    } catch {
      // Try query method if direct call fails
      const queryParams = loanApi.query().withLoanPid(loanId).qs();
      const response = await loanApi.list(queryParams);
      if (response.data.hits?.length > 0) {
        loan = response.data.hits[0];
      }
    }

    if (!loan) return "ITEM_RETURNED";

    const status =
      loan.metadata?.state ||
      loan.state ||
      loan.status ||
      loan.metadata?.status ||
      "UNKNOWN";

    if (status === "UNKNOWN" && loan) {
      if (
        loan.metadata?.transaction_date ||
        loan.returned_date ||
        loan.end_date
      ) {
        return "ITEM_RETURNED";
      }
      if (loan.metadata?.start_date && !loan.metadata?.actual_end_date) {
        return "ITEM_ON_LOAN";
      }
    }

    return status;
  } catch {
    return "ITEM_RETURNED";
  }
};

/**
 * Handle file upload and convert to base64 preview
 * @param {File|FileList|Event} imageFile - File input or file object
 * @param {Function} onSuccess - Callback for successful file read
 * @param {Function} onError - Callback for error handling
 */
export const handleFileUpload = (imageFile, onSuccess, onError) => {
  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => onSuccess(e.target.result);
    reader.onerror = () => onError(DEFAULT_MESSAGES.ERROR_FILE_READ);
    reader.readAsDataURL(file);
  };

  let file = null;
  if (imageFile?.target?.files?.[0]) {
    file = imageFile.target.files[0];
  } else if (imageFile instanceof File || imageFile instanceof Blob) {
    file = imageFile;
  }

  if (file && (file instanceof File || file instanceof Blob)) {
    handleFile(file);
  } else {
    onError("Please select a valid image file");
  }
};

/**
 * Format patron data for modals
 * @param {Object} loanData - Loan data object
 * @param {Object} patronData - Patron data object (optional)
 * @returns {Object} Formatted patron object for modal display
 */
export const formatPatronForModal = (loanData, patronData = null) => {
  const overdueDays = calculateOverdueDays(loanData.metadata.end_date);
  const calculatedFine = calculateOverdueFine(loanData.metadata.end_date);

  return {
    name: loanData.metadata.patron?.name || patronData?.name || "Unknown",
    id: loanData.metadata.patron_pid || "N/A",
    email:
      patronData?.email ||
      loanData.metadata.patron?.email ||
      "unknown@example.com",
    loans: loanData.loans || [
      {
        metadata: {
          pid: loanData.metadata.pid,
          start_date: loanData.metadata.start_date,
          end_date: loanData.metadata.end_date,
          document: loanData.metadata.document,
        },
        calculatedFine,
        overdueDays,
        dueDate: loanData.metadata.end_date,
        title: loanData.metadata.document?.title || "Unknown Title",
        author:
          loanData.metadata.document?.authors?.[0]?.full_name ||
          "Unknown Author",
      },
    ],
  };
};

/**
 * Process fine data from API response
 * @param {Array} fines - Array of fine records from API
 * @returns {Array} Processed patron data with aggregated loans and fines
 */
// Add this to your fineUtils.js file or update the existing processFineData function:

// Fix the processFineData function - it needs to handle cases without fines:
export const processFineData = (fines, calculateLoanFine) => {
  if (!fines || fines.length === 0) {
    return [];
  }

  const patronsMap = new Map();

  fines.forEach((fine) => {
    // Extract patron ID from multiple possible sources
    let patronId =
      fine.patron_pid ||
      fine.metadata?.patron?.pid ||
      fine.patron?.pid ||
      fine.id;

    if (!patronId) {
      return;
    }

    if (!patronsMap.has(patronId)) {
      patronsMap.set(patronId, {
        pid: patronId,
        name:
          fine.name ||
          fine.patron_name ||
          fine.metadata?.patron?.name ||
          fine.patron?.name ||
          `Patron ${patronId}`,
        email:
          fine.email ||
          fine.patron_email ||
          fine.metadata?.patron?.email ||
          fine.patron?.email ||
          "Email not available",
        totalFine: 0,
        overdueCount: 0,
        loans: [],
        fineRecords: [],
      });
    }

    const patron = patronsMap.get(patronId);

    // Add fine record
    patron.fineRecords.push({
      id: fine.id,
      loan_id: fine.loan_id,
      total_fine: parseFloat(fine.total_fine || fine.amount || 0),
      status: fine.status || "active",
    });

    // Update total fine
    patron.totalFine += parseFloat(fine.total_fine || fine.amount || 0);

    // Create loan object
    if (fine.loan_id) {
      const existingLoan = patron.loans.find(
        (loan) => loan.metadata?.pid === fine.loan_id
      );

      if (!existingLoan) {
        const endDate =
          fine.due_date ||
          fine.end_date ||
          patron.loans.push({
            metadata: {
              pid: fine.loan_id,
              patron: {
                name:
                  fine.name ||
                  fine.patron_name ||
                  fine.metadata?.patron?.name ||
                  patron.name,
                email:
                  fine.email ||
                  fine.patron_email ||
                  fine.metadata?.patron?.email ||
                  patron.email,
                pid: patronId,
              },
              document: {
                title:
                  fine.item_title || fine.document_title || "Unknown Title",
              },
              item: {
                barcode: fine.item_barcode || "",
              },
              end_date: endDate,
            },
            calculatedFine: parseFloat(fine.total_fine || 0),
            overdue_days: fine.overdue_days || 0,
          });

        patron.overdueCount += 1;
      }
    }
  });

  return Array.from(patronsMap.values()).filter((patron) => patron.pid);
};

/**
 * Extract form data from FormData object
 * @param {FormData} formData - Form data object
 * @returns {Object} Extracted data object
 */
export const extractFormData = (formData) => ({
  patronId: formData.get("profile_id"),
  amount: parseFloat(formData.get("amount")),
  paymentReceipt:
    formData.get("payment_receipt") || formData.get("payment_reference"),
  selectedLoanIds: JSON.parse(formData.get("loan_ids") || "[]"),
});

/**
 * Generate payment result message
 * @param {Object} results - Payment processing results
 * @param {Array} results.processedFines - Successfully processed fines
 * @param {Array} results.failedFines - Failed fine processing attempts
 * @returns {Object} Result object with success status and message
 */
export const generatePaymentResult = ({ processedFines, failedFines }) => {
  const successCount = processedFines.length;
  const failureCount = failedFines.length;

  if (successCount === 0) {
    throw new Error(`Payment processing failed for all ${failureCount} fines.`);
  }

  return {
    success: true,
    message:
      failureCount === 0
        ? `Payment processed! All ${successCount} fines have been paid.`
        : `Partial success: ${successCount} fines paid, ${failureCount} failed.`,
    processed_fines: successCount,
    failed_fines: failureCount,
    details: { successful: processedFines, failed: failedFines },
  };
};

/**
 * Debounce function for search inputs
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Validate payment form data
 * @param {Object} params - Payment validation parameters
 * @param {string|File} params.paymentImagePreview - Payment image or reference
 * @param {Array} params.selectedBooks - Selected books for payment
 * @returns {Object} Validation result with isValid flag and error message
 */
export const validatePaymentData = ({ paymentImagePreview, selectedBooks }) => {
  if (!paymentImagePreview) {
    return {
      isValid: false,
      error: DEFAULT_MESSAGES.ERROR_NO_IMAGE,
    };
  }

  if (!selectedBooks || selectedBooks.length === 0) {
    return {
      isValid: false,
      error: DEFAULT_MESSAGES.ERROR_NO_BOOKS,
    };
  }

  return { isValid: true, error: null };
};

/**
 * Create safe component state updater that checks if component is mounted
 * @param {Object} component - React component instance
 * @returns {Function} Safe setState function
 */
export const createSafeStateUpdater = (component) => {
  return (stateUpdate) => {
    if (component._isMounted) {
      component.setState(stateUpdate);
    }
  };
};

/**
 * Processes fine history data for display in the history table
 * @param {Array} fines - Array of fine records from the API
 * @returns {Array} Processed fine history data
 */
export const processFineHistoryData = (fines) => {
  return fines
    .map((fine) => ({
      id: fine.id,
      loan_id: fine.loan_id,
      patron_name: fine.patron_name || fine.name,
      patron_email: fine.patron_email || fine.email,
      patron_pid: fine.patron_pid,
      document_title: fine.document_title || fine.item_title,
      item_barcode: fine.item_barcode,
      due_date: fine.due_date || fine.start_date,
      return_date: fine.return_date,
      total_fine: parseFloat(fine.total_fine || 0),
      calculated_fine: parseFloat(fine.calculated_fine || 0),
      status: fine.status,
      created_date: fine.created_date,
      updated_date: fine.updated_date,
      created_by: fine.created_by,
      updated_by: fine.updated_by,
      payment_reference: fine.payment_reference,
      notes: fine.notes,
      // Calculate additional fields if needed
      days_overdue: fine.due_date ? calculateOverdueDays(fine.due_date) : 0,
      is_resolved: ["resolved", "paid", "waived", "cancelled"].includes(
        fine.status
      ),
    }))
    .sort((a, b) => {
      // Sort by updated_date descending (most recent first)
      return (
        new Date(b.updated_date || b.created_date) -
        new Date(a.updated_date || a.created_date)
      );
    });
};
